import React, { useState, useEffect, useRef } from "react";
import "./EnhancePlusFilterModal.css";

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TuneIcon from "@mui/icons-material/Tune";

import MultiSelectDropdown from "../../../EnhanceFilters/MultiSelectDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  getEpisodeIdentifiers,
  getSpecialitiesFilter,
  getDrg,
  getFileNames,
  getPrincipalDiagnosis,
  getHealthFund,
  REQUEST_STATUS,
  getPrincipalProcedureCodes,
  getAuditRuleRefNo,
  getPredictedDRG1,
  getPredictedDRG2
} from "../../../EnhanceFilters/EnhanceFiltersSlice";
import {updatedEnhancePlusPayload } from "../EnhancePlusWorkflowSlice";
import { formatDate } from "../../../GenericTable/ColumnFilter";
import FilterBreadcrumbs, {
  FILTER_LABELS,
} from "../../../GenericTable/FilterBreadcrumbs";
import { getAssignUserMenuData } from "../../../workflowActions/WorkflowSiderbarSlice";

// Local dummy data for items without API endpoints
const pallasPriorities = ["Ultra High", "High"];
const procedureCodes = ["P001", "P002", "P003", "P004"];
const auditFlaggedStatuses = [
  "All Statuses",
  "Flagged",
  "Not Flagged",
  "Under Review",
];
const drgVersions = [
  { label: "v10.0", value: 10 },
  { label: "v9.0", value: 9 },
  { label: "v8.0", value: 8 },
  { label: "v11.0", value: 11 },
];
// Operator options for display
const operatorOptions = [">", "<", ">=", "<=", "=", "!="];

// Single operator mapping
// const OPERATOR_MAP = {
//   "<": "lt",
//   ">": "gt",
//   "<=": "lte",
//   ">=": "gte",
//   "=": "eq",
//   "!=": "ne",
// };

// // Single helper function to map operator
// const mapOperator = (operator) => {
//   return OPERATOR_MAP[operator] || operator;
// };
// const REVERSE_OPERATOR_MAP = Object.fromEntries(
//   Object.entries(OPERATOR_MAP).map(([symbol, apiVal]) => [apiVal, symbol]),
// );

// const mapOperatorToSymbol = (apiVal) => {
//   return REVERSE_OPERATOR_MAP[apiVal] || apiVal;
// };

const initialState = {
  priority: "",
  episodeId: [],
  speciality: [],
  actualDrg: [],
  predictedDrg1: [],
  predictedDrg2: [],
  diagnosis: [],
  procedureCode: [],
  admissionFrom: "",
  admissionTo: "",
  separationFrom: "",
  separationTo: "",
  auditFlaggedStatus: [],
  auditRef: "",
  drgVersion: [],
  healthFundId: [],
  losOperator: "",
  los: "",
  ageOperator: "",
  age: "",
  assignedTo: [],
  file:[],
};

const FieldLabel = ({ children }) => (
  <Typography className="enhance-filter-plus-field-label">
    {children}
  </Typography>
);

const EnhancePlusFilterModal = ({ open, onClose, version, setVersion}) => {
  const [filters, setFilters] = useState(initialState);
  const prevOpenRef = React.useRef(false);
  const fileNameToHeaderIdsRef = useRef({});
  const [dropdownState, setDropdownState] = useState({
    episode: { page: 1, search: "" },
    file: { page: 1, search: "" },
    speciality: { page: 1, search: "" },
    drg: { page: 1, search: "" },
    diagnosis: { page: 1, search: "" },
    healthFund: { page: 1, search: "" },
    principalProcedureCodes: { page: 1, search: "" },
    auditRuleReferenceNo: { page: 1, search: "" },
    predictedDrg1:{page:1,search:""},
    predictedDrg2:{page:1,search:""}
  });

  const dispatch = useDispatch();
  const dropdowns = useSelector((state) => state.enhanceFilters.dropdowns);
  const currentPayload = useSelector(
    (state) => state.enhancePlusWorkflow.payload,
  );
  // console.log(currentPayload, "currentPayload");
  const {
    episode,
    specialities,
    drg,
    file,
    principalDiagnosis,
    healthfund,
    principalProcedureCodes,
    auditRuleReferenceNo,
    predictedDrg1,
    predictedDrg2
  } = dropdowns;

  const isAgeDisabled =
    !filters.ageOperator || filters.ageOperator.length === 0;
  const isLosDisabled =
    !filters.losOperator || filters.losOperator.length === 0;

  // Determine if "to" date fields should be disabled
  const isAdmissionToDisabled = !filters.admissionFrom;
  const isSeparationToDisabled = !filters.separationFrom;
  const assignedToState = useSelector(
  (state) => state.workflowsidebar?.files
) ?? { data: [], hasMore: true, status: REQUEST_STATUS.IDLE, error: null };

const [assignedToQuery, setAssignedToQuery] = useState({ page: 1, search: "" });

const assignedToNameToIdRef = useRef({});
const assignedToIdToNameRef = useRef({});

useEffect(() => {
  if (assignedToState?.data?.length) {
    assignedToState.data.forEach((u) => {
      assignedToNameToIdRef.current[u.username] = u.user_id;
      assignedToIdToNameRef.current[u.user_id] = u.username;
    });
  }
}, [assignedToState?.data]);

  // Load dropdown data when modal opens
  useEffect(() => {
    if (!open) return;

    const loadDropdown = (key, thunk) => {
      setDropdownState((prev) => ({
        ...prev,
        [key]: { page: 1, search: "" },
      }));

      dispatch(thunk({ search: "", page_number: 1, page_size: 10 }));
    };

    loadDropdown("episode", getEpisodeIdentifiers);
    loadDropdown("speciality", getSpecialitiesFilter);
    loadDropdown("file", getFileNames);
    loadDropdown("drg", getDrg);
    loadDropdown("predictedDrg1", getPredictedDRG1);
    loadDropdown("predictedDrg2", getPredictedDRG2);
    loadDropdown("diagnosis", getPrincipalDiagnosis);
    loadDropdown("healthFund", getHealthFund);
    loadDropdown("principalProcedureCodes", getPrincipalProcedureCodes);
    loadDropdown("auditRuleReferenceNo", getAuditRuleRefNo);
    setAssignedToQuery({ page: 1, search: "" });
    dispatch(getAssignUserMenuData({ pageNumber: 1, search: "" }));
  }, [open, dispatch]);

  const handleSearch = (key, thunk) => (_, value) => {
    setDropdownState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        search: value,
        page: 1,
      },
    }));

    dispatch(
      thunk({
        search: value,
        page_number: 1,
        page_size: 10,
      }),
    );
  };
  useEffect(() => {
    if (file?.data?.length) {
      file.data.forEach((f) => {
        if (!fileNameToHeaderIdsRef.current[f.file_name]) {
          fileNameToHeaderIdsRef.current[f.file_name] = new Set();
        }
        fileNameToHeaderIdsRef.current[f.file_name].add(f.header_id);
      });
    }
  }, [file?.data]);
  const getModalOwnedIds = () => {
  const s = new Set();
  Object.values(fileNameToHeaderIdsRef.current).forEach((set) =>
    set.forEach((id) => s.add(id)),
  );
  return s;
};
const getModalOwnedNames = () => new Set(Object.keys(fileNameToHeaderIdsRef.current))

  const handleLoadMore = (key, thunk, hasMore) => () => {
    if (!hasMore) return;

    const nextPage = dropdownState[key].page + 1;

    dispatch(
      thunk({
        search: dropdownState[key].search,
        page_number: nextPage,
        page_size: 10,
      }),
    );

    setDropdownState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        page: nextPage,
      },
    }));
  };
  const handleInput = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setFilters(initialState);
  };

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!justOpened) return;

    setFilters({
      priority: Array.isArray(currentPayload.pallas_priority)
        ? currentPayload.pallas_priority[0] || ""
        : currentPayload.pallas_priority || currentPayload.priority || "",
      episodeId: currentPayload.episode_id || [],
      speciality: currentPayload.speciality || [],
      file: currentPayload.file_type || [],
      actualDrg: currentPayload.actual_drg || [],
      predictedDrg1: currentPayload.predicted_drg1 || [],
      predictedDrg2: currentPayload.predicted_drg2 || [],
      diagnosis: currentPayload.principal_diagnosis || [],
      procedureCode: currentPayload.principal_procedure_code || [],
      admissionFrom: formatDate(currentPayload.admission_date_from) || "",
      admissionTo: formatDate(currentPayload.admission_date_to) || "",
      separationFrom: formatDate(currentPayload.separation_date_from) || "",
      separationTo: formatDate(currentPayload.separation_date_to) || "",
      auditFlaggedStatus:
        currentPayload.audit_rule_flagged_status === true
          ? ["Flagged"]
          : currentPayload.audit_rule_flagged_status === false
            ? []
            : [],
      auditRef: currentPayload.audit_rule_reference_no || [],
      drgVersion: currentPayload.drg_version
        ? [currentPayload.drg_version]
        : [],
      healthFundId: currentPayload.health_fund_identifier || [],
      losOperator: currentPayload.los_op || "", 
      los: currentPayload.los || "",
      ageOperator: currentPayload.age_op || "", 
      age: currentPayload.age || "",
      version:currentPayload.version || "",
      assignedTo: (currentPayload.assigned_to || []).map(
  (id) => assignedToIdToNameRef.current[id] || id
),
    });
  }, [open, currentPayload]);

  const handleApply = () => {
   const modalOwnedIds = getModalOwnedIds();
  const modalOwnedNames = getModalOwnedNames();
    // — i.e. it came from the toolbar's "Select Files" menu — must be preserved
  const preservedHeaderIds = (currentPayload.header_id || []).filter(
    (id) => !modalOwnedIds.has(id),
  );
    const preservedFileTypes = (currentPayload.file_type || []).filter(
    (name) => !modalOwnedNames.has(name),
  );
    const selectedAssignedToIds = filters.assignedTo
  .map((name) => assignedToNameToIdRef.current[name])
  .filter((id) => id !== undefined);

  // this modal's own current selection
  const selectedHeaderIds = Array.from(
    new Set(
      filters.file.flatMap(
        (name) => Array.from(fileNameToHeaderIdsRef.current[name] || []),
      ),
    ),
  );
    const payload = {
      episode_id: filters.episodeId || [],
      pallas_priority: filters.priority ? [filters.priority] : [],
      speciality: filters.speciality,
      file_type: Array.from(new Set([...preservedFileTypes, ...filters.file])),
      header_id: Array.from(new Set([...preservedHeaderIds, ...selectedHeaderIds])),
      actual_drg: filters.actualDrg,
      predicted_drg1: filters.predictedDrg1,
      predicted_drg2: filters.predictedDrg2,
      principal_diagnosis: filters.diagnosis,
      principal_procedure_code: filters.procedureCode,
      admission_date_from: formatDate(filters.admissionFrom),
      admission_date_to: formatDate(filters.admissionTo),
      separation_date_from: formatDate(filters.separationFrom),
      separation_date_to: formatDate(filters.separationTo),
      audit_rule_flagged_status:
        filters.auditFlaggedStatus.length > 0
          ? filters.auditFlaggedStatus[0] === "Flagged"
          : false,
      audit_rule_reference_no: filters.auditRef,
      health_fund_identifier: filters.healthFundId,
      drg_version: filters.drgVersion.length > 0 ? filters.drgVersion[0] : "",
      los_op: filters.losOperator || "",
      los: filters.los || "",
      age_op: filters.ageOperator || "",
      age: filters.age || "",
      page_number: 1,
      page_size: currentPayload.page_size, 
      version: filters.version ||"",
       assigned_to: selectedAssignedToIds,
    };

    dispatch(updatedEnhancePlusPayload(payload));
    console.log("Applied Filters:", payload);
    onClose();
  };
  const handleAssignedToSearch = (_, value) => {
  setAssignedToQuery({ search: value, page: 1 });
  dispatch(getAssignUserMenuData({ pageNumber: 1, search: value }));
};

const handleAssignedToLoadMore = () => {
  if (!assignedToState.hasMore) return;
  const nextPage = assignedToQuery.page + 1;
  dispatch(getAssignUserMenuData({ pageNumber: nextPage, search: assignedToQuery.search }));
  setAssignedToQuery((prev) => ({ ...prev, page: nextPage }));
};

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close when explicitly triggered (like clicking the X button)
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      fullWidth
      maxWidth="md"
      classes={{ paper: "enhance-filter-plus-modal" }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0,0,0,0.5)" },
        },
      }}
    >
      <Box className="enhance-filter-plus-header">
        <Box>
          <Typography className="enhance-filter-plus-title">
            Filter Records
          </Typography>
          <Typography className="enhance-filter-plus-subtitle">
            Refine the Enhance Plus optimization workspace.
          </Typography>
          <Box mt={1}>
            <FilterBreadcrumbs
              filters={filters}
              labels={FILTER_LABELS}
              onRemove={(key, value) => {
                setFilters((prev) => ({
                  ...prev,
                  [key]: Array.isArray(prev[key])
                    ? prev[key].filter((item) => item !== value)
                    : "",
                }));
              }}
            />
          </Box>
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className="enhance-filter-plus-body">
        <Box className="enhance-filter-plus-scroll-area">
          <Typography className="enhance-filter-plus-section-heading-first">
            OPTIMIZATION FILTERS
          </Typography>

          <Box className="enhance-filter-plus-row enhance-filter-plus-row-1">
            <Box className="enhance-filter-plus-field">
              <FieldLabel>Pallas Priority</FieldLabel>
              <Box className="enhance-filter-plus-priority-group">
                {pallasPriorities.map((p) => (
                  <button
                    type="button"
                    key={p}
                    className={`enhance-filter-plus-priority-btn ${
                      filters.priority === p
                        ? "enhance-filter-plus-priority-active"
                        : ""
                    }`}
                    onClick={() => handleInput("priority", p)}
                  >
                    {p}
                  </button>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography className="enhance-filter-plus-section-heading">
            EPISODE FILTERS
          </Typography>

          <Box className="enhance-filter-plus-row enhance-filter-plus-row-2">
            <Box className="enhance-filter-plus-field">
              <MultiSelectDropdown
                label="Episode ID"
                placeholder="Select Episode"
                options={episode?.data}
                value={filters.episodeId}
                onChange={(value) => handleInput("episodeId", value)}
                loading={
                  episode.status === REQUEST_STATUS.LOADING &&
                  dropdownState.episode.page === 1
                }
                loadingMore={
                  episode.status === REQUEST_STATUS.LOADING &&
                  dropdownState.episode.page > 1
                }
                hasMore={episode.hasMore}
                onLoadMore={handleLoadMore(
                  "episode",
                  getEpisodeIdentifiers,
                  episode.hasMore,
                )}
                onInputChange={handleSearch("episode", getEpisodeIdentifiers)}
              />
            </Box>

            <MultiSelectDropdown
              label="Speciality"
              placeholder="Select specialty"
              options={specialities?.data}
              value={filters.speciality}
              onChange={(value) => handleInput("speciality", value)}
              loading={
                specialities.status === REQUEST_STATUS.LOADING &&
                dropdownState.speciality.page === 1
              }
              loadingMore={
                specialities.status === REQUEST_STATUS.LOADING &&
                dropdownState.speciality.page > 1
              }
              hasMore={specialities.hasMore}
              onLoadMore={handleLoadMore(
                "speciality",
                getSpecialitiesFilter,
                specialities.hasMore,
              )}
              onInputChange={handleSearch("speciality", getSpecialitiesFilter)}
            />
            <MultiSelectDropdown
              label="Assigned To"
              placeholder="Search Users"
              options={assignedToState.data.map((u) => u.username)}
              value={filters.assignedTo}
              onChange={(value) => handleInput("assignedTo", value)}
              loading={
                assignedToState.status === REQUEST_STATUS.LOADING &&
                assignedToQuery.page === 1
              }
              loadingMore={
                assignedToState.status === REQUEST_STATUS.LOADING &&
                assignedToQuery.page > 1
              }
              hasMore={assignedToState.hasMore}
              onLoadMore={handleAssignedToLoadMore}
              onInputChange={handleAssignedToSearch}
            />
            <MultiSelectDropdown
              label="File Name"
              placeholder="Search File"
              options={[...new Set(file.data.map((f) => f.file_name))]}
              value={filters.file}
              onChange={(value) => handleInput("file", value)}
              loading={file.status === REQUEST_STATUS.LOADING && dropdownState.file.page === 1}
              loadingMore={file.status === REQUEST_STATUS.LOADING && dropdownState.file.page > 1}
              hasMore={file.hasMore}
              onLoadMore={handleLoadMore("file", getFileNames, file.hasMore)}
              loadWhenNotScrollable  
              onInputChange={handleSearch("file", getFileNames)}
            />
          </Box>

          <Typography className="enhance-filter-plus-section-heading">
            DRG FILTERS
          </Typography>

          <Box className="enhance-filter-plus-row enhance-filter-plus-row-3">
            <MultiSelectDropdown
              label="Actual DRG"
              placeholder="Search DRG"
              options={drg.data}
              value={filters.actualDrg}
              onChange={(value) => handleInput("actualDrg", value)}
              loading={
                drg.status === REQUEST_STATUS.LOADING &&
                dropdownState.drg.page === 1
              }
              loadingMore={
                drg.status === REQUEST_STATUS.LOADING &&
                dropdownState.drg.page > 1
              }
              hasMore={drg.hasMore}
              onLoadMore={handleLoadMore("drg", getDrg, drg.hasMore)}
              onInputChange={handleSearch("drg", getDrg)}
            />

            <MultiSelectDropdown
              label="Predicted DRG 1"
              placeholder="Search DRG"
              options={predictedDrg1.data}
              value={filters.predictedDrg1}
              onChange={(value) => handleInput("predictedDrg1", value)}
              loading={
                predictedDrg1.status === REQUEST_STATUS.LOADING &&
                dropdownState.predictedDrg1.page === 1
              }
              loadingMore={
                predictedDrg1.status === REQUEST_STATUS.LOADING &&
                dropdownState.predictedDrg1.page > 1
              }
              hasMore={predictedDrg1.hasMore}
              onLoadMore={handleLoadMore("predictedDrg1", getPredictedDRG1, predictedDrg1.hasMore)}
              onInputChange={handleSearch("predictedDrg1", getPredictedDRG1)}
            />

            <MultiSelectDropdown
              label="Predicted DRG 2"
              placeholder="Search DRG"
              options={predictedDrg2.data}
              value={filters.predictedDrg2}
              onChange={(value) => handleInput("predictedDrg2", value)}
              loading={
                predictedDrg2.status === REQUEST_STATUS.LOADING &&
                dropdownState.predictedDrg2.page === 1
              }
              loadingMore={
                predictedDrg2.status === REQUEST_STATUS.LOADING &&
                dropdownState.predictedDrg2.page > 1
              }
              hasMore={predictedDrg2.hasMore}
              onLoadMore={handleLoadMore("predictedDrg2", getPredictedDRG2, predictedDrg2.hasMore)}
              onInputChange={handleSearch("predictedDrg2", getPredictedDRG2)}
            />
          </Box>

          <Typography className="enhance-filter-plus-section-heading">
            CLINICAL FILTERS
          </Typography>

          <Box className="enhance-filter-plus-row enhance-filter-plus-row-2">
            <MultiSelectDropdown
              label="Principal Diagnosis"
              placeholder="Search Diagnosis Code.."
              options={principalDiagnosis?.data}
              value={filters.diagnosis}
              onChange={(value) => handleInput("diagnosis", value)}
              loading={
                principalDiagnosis.status === REQUEST_STATUS.LOADING &&
                dropdownState.diagnosis.page === 1
              }
              loadingMore={
                principalDiagnosis.status === REQUEST_STATUS.LOADING &&
                dropdownState.diagnosis.page > 1
              }
              hasMore={principalDiagnosis.hasMore}
              onLoadMore={handleLoadMore(
                "diagnosis",
                getPrincipalDiagnosis,
                principalDiagnosis.hasMore,
              )}
              onInputChange={handleSearch("diagnosis", getPrincipalDiagnosis)}
            />

            <MultiSelectDropdown
              label="Principal Procedure Code"
              placeholder="Search Procedure Code..."
              options={principalProcedureCodes.data}
              value={filters.procedureCode}
              onChange={(value) => handleInput("procedureCode", value)}
              loading={
                principalProcedureCodes.status === REQUEST_STATUS.LOADING &&
                dropdownState.principalProcedureCodes.page === 1
              }
              loadingMore={
                principalProcedureCodes.status === REQUEST_STATUS.LOADING &&
                dropdownState.principalProcedureCodes.page > 1
              }
              hasMore={principalProcedureCodes.hasMore}
              onLoadMore={handleLoadMore(
                "principalProcedureCodes",
                getPrincipalProcedureCodes,
                principalProcedureCodes.hasMore,
              )}
              onInputChange={handleSearch(
                "principalProcedureCodes",
                getPrincipalProcedureCodes,
              )}
            />
          </Box>

          <Typography className="enhance-filter-plus-section-heading">
            DATE FILTERS
          </Typography>

          <Box className="enhance-filter-plus-row enhance-filter-plus-row-2">
            <Box className="enhance-filter-plus-date-group">
              <FieldLabel>Admission Date</FieldLabel>
              <Box className="enhance-filter-plus-date-group-inputs">
                {/* Admission From - No Tooltip */}
                <TextField
                  type="date"
                  value={filters.admissionFrom}
                  onChange={(e) => handleInput("admissionFrom", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ notched: false }}
                  fullWidth
                />
                <Typography className="enhance-filter-plus-date-group-to">
                  to
                </Typography>
                {/* Admission To - Tooltip only when disabled */}
                <Tooltip
                  title={
                    isAdmissionToDisabled
                      ? "Please select a 'From' date first"
                      : ""
                  }
                  arrow
                  disableHoverListener={!isAdmissionToDisabled}
                >
                  <span style={{ width: "100%" }}>
                    <TextField
                      type="date"
                      value={filters.admissionTo}
                      onChange={(e) =>
                        handleInput("admissionTo", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        notched: false,
                        inputProps: {
                          min: filters.admissionFrom || undefined, // Restrict to dates after admissionFrom
                        },
                      }}
                      disabled={isAdmissionToDisabled}
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root.Mui-disabled": {
                          backgroundColor: "#f5f5f5",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box className="enhance-filter-plus-date-group">
              <FieldLabel>Separation Date</FieldLabel>
              <Box className="enhance-filter-plus-date-group-inputs">
                {/* Separation From - No Tooltip */}
                <TextField
                  type="date"
                  value={filters.separationFrom}
                  onChange={(e) =>
                    handleInput("separationFrom", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ notched: false }}
                  fullWidth
                />
                <Typography className="enhance-filter-plus-date-group-to">
                  to
                </Typography>
                {/* Separation To - Tooltip only when disabled */}
                <Tooltip
                  title={
                    isSeparationToDisabled
                      ? "Please select a 'From' date first"
                      : ""
                  }
                  arrow
                  disableHoverListener={!isSeparationToDisabled}
                >
                  <span style={{ width: "100%" }}>
                    <TextField
                      type="date"
                      value={filters.separationTo}
                      onChange={(e) =>
                        handleInput("separationTo", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        notched: false,
                        inputProps: {
                          min: filters.separationFrom || undefined, // Restrict to dates after separationFrom
                        },
                      }}
                      disabled={isSeparationToDisabled}
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root.Mui-disabled": {
                          backgroundColor: "#f5f5f5",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Accordion defaultExpanded className="enhance-filter-plus-advanced">
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
              <Box display="flex" alignItems="center" gap={1}>
                <TuneIcon fontSize="small" />
                <Typography className="enhance-filter-plus-advanced-title">
                  Advanced Filters
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box className="enhance-filter-plus-row enhance-filter-plus-row-2">
                <MultiSelectDropdown
                  label="Audit Rule Reference No"
                  placeholder="Search Reference No"
                  options={auditRuleReferenceNo.data}
                  value={filters.auditRef}
                  onChange={(value) => handleInput("auditRef", value)}
                  loading={
                    auditRuleReferenceNo.status === REQUEST_STATUS.LOADING &&
                    dropdownState.auditRuleReferenceNo.page === 1
                  }
                  loadingMore={
                    auditRuleReferenceNo.status === REQUEST_STATUS.LOADING &&
                    dropdownState.auditRuleReferenceNo.page > 1
                  }
                  hasMore={auditRuleReferenceNo.hasMore}
                  onLoadMore={handleLoadMore(
                    "auditRuleReferenceNo",
                    getAuditRuleRefNo,
                    auditRuleReferenceNo.hasMore,
                  )}
                  onInputChange={handleSearch(
                    "auditRuleReferenceNo",
                    getAuditRuleRefNo,
                  )}
                />
                <MultiSelectDropdown
                  label="Health Fund Identifier"
                  placeholder="Select health fund"
                  options={healthfund.data}
                  value={filters.healthFundId}
                  onChange={(value) => handleInput("healthFundId", value)}
                  loading={
                    healthfund.status === REQUEST_STATUS.LOADING &&
                    dropdownState.healthFund.page === 1
                  }
                  loadingMore={
                    healthfund.status === REQUEST_STATUS.LOADING &&
                    dropdownState.healthFund.page > 1
                  }
                  hasMore={healthfund.hasMore}
                  onLoadMore={handleLoadMore(
                    "healthFund",
                    getHealthFund,
                    healthfund.hasMore,
                  )}
                  onInputChange={handleSearch("healthFund", getHealthFund)}
                />
              </Box>
{/* 
              <Box className="enhance-filter-plus-row enhance-filter-plus-row-2">
                <MultiSelectDropdown
                  label="Health Fund Identifier"
                  placeholder="Select health fund"
                  options={healthfund.data}
                  value={filters.healthFundId}
                  onChange={(value) => handleInput("healthFundId", value)}
                  loading={
                    healthfund.status === REQUEST_STATUS.LOADING &&
                    dropdownState.healthFund.page === 1
                  }
                  loadingMore={
                    healthfund.status === REQUEST_STATUS.LOADING &&
                    dropdownState.healthFund.page > 1
                  }
                  hasMore={healthfund.hasMore}
                  onLoadMore={handleLoadMore(
                    "healthFund",
                    getHealthFund,
                    healthfund.hasMore,
                  )}
                  onInputChange={handleSearch("healthFund", getHealthFund)}
                />
              </Box> */}

              <Box className="enhance-filter-plus-row enhance-filter-plus-row-4">
                <MultiSelectDropdown
                  multiple={false}
                  label="LOS Operator"
                  placeholder="Select operator"
                  options={operatorOptions}
                  value={filters.losOperator}
                  onChange={(value) => handleInput("losOperator", value)}
                />

                <Box className="enhance-filter-plus-field">
                  <FieldLabel>Length of Stay (LOS)</FieldLabel>
                  <TextField
                    placeholder="Enter days"
                    value={filters.los}
                    onChange={(e) => handleInput("los", e.target.value)}
                    disabled={isLosDisabled}
                    className={isLosDisabled ? "age-disabled-field" : ""}
                    fullWidth
                    InputProps={{ notched: false }}
                  />
                </Box>

                <MultiSelectDropdown
                  multiple={false}
                  label="Age Operator"
                  placeholder="Select operator"
                  options={operatorOptions}
                  value={filters.ageOperator}
                  onChange={(value) => handleInput("ageOperator", value)}
                />

                <Box className="enhance-filter-plus-field">
                  <FieldLabel>Age Range</FieldLabel>
                  <TextField
                    placeholder="Enter age"
                    value={filters.age}
                    onChange={(e) => handleInput("age", e.target.value)}
                    disabled={isAgeDisabled}
                    className={isAgeDisabled ? "age-disabled-field" : ""}
                    fullWidth
                    InputProps={{ notched: false }}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box className="enhance-filter-plus-footer">
          <Button
            size="small"
            startIcon={<RestartAltIcon fontSize="small" />}
            color="error"
            onClick={handleClear}
          >
            Clear All
          </Button>

          <Box display="flex" gap={1.5}>
            <Button size="small" variant="outlined" onClick={onClose}>
              Cancel
            </Button>

            <Button
              size="small"
              variant="contained"
              className="enhance-filter-plus-apply-btn"
              onClick={handleApply}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancePlusFilterModal;
